// @file Vue composable for all attachment related logic in Slideshow

import { getCurrentPostCid } from '@@/bits/slideshow'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Page } from '@@/types/slideshow'
import { PageType } from '@@/types/slideshow'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'

export const useSlideshowAttachments = (): {
  postCidsForLoadedAttachments: Ref<Set<string>>
  addPostCidToLoadedAttachments: (attachmentLoadedPostCid: string) => void
  hasAttachmentsOnPage: (page: Page) => boolean
  areAttachmentsLoadedForCurrentPost: Ref<boolean>
  numberOfWordsOnPage: Ref<number>
  numberOfAttachmentsOnPage: Ref<number>
} => {
  const { postEntitiesByCid } = storeToRefs(useSurfacePostsStore())
  const {
    postCidsForLoadedAttachments,
    areAttachmentsLoadedForCurrentPost,
    numberOfWordsOnPage,
    numberOfAttachmentsOnPage,
  } = storeToRefs(useSlideshowStore())

  const addPostCidToLoadedAttachments = (attachmentLoadedPostCid: string): void => {
    if (postCidsForLoadedAttachments.value.has(attachmentLoadedPostCid)) {
      return
    }
    const updatedPostCidsForLoadedAttachments = new Set(postCidsForLoadedAttachments.value)
    updatedPostCidsForLoadedAttachments.add(attachmentLoadedPostCid)
    postCidsForLoadedAttachments.value = updatedPostCidsForLoadedAttachments
  }

  const hasAttachmentsOnPage = (page: Page): boolean => {
    if (page?.pageType !== PageType.Post || page.entityId == null) return false
    const post = postEntitiesByCid.value[getCurrentPostCid(page.entityId)]
    return post.attachment != null && post.attachment !== ''
  }

  return {
    postCidsForLoadedAttachments,
    areAttachmentsLoadedForCurrentPost,
    numberOfWordsOnPage,
    numberOfAttachmentsOnPage,
    hasAttachmentsOnPage,
    addPostCidToLoadedAttachments,
  }
}
